import { ApiErrorStatusCode } from 'lib-shared-auth';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import type { AsyncStatus } from 'lib-shared-types-api';
import { useEffect, useMemo, useState } from 'react';

import { analyticsApi } from 'apis/analytics';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useServerStorage } from 'utils/useServerStorage';

import { analyticsActions } from '../../../../analytics.actions';

import { UseQuickViewInitResult } from './useQuickViewInit.types';

/** A hook for fetching a quick view based on the id in the URL path params */
export const useQuickViewInit = (): UseQuickViewInitResult => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const params = useAppPathParams('/analytics/dashboard/custom/:id');
  const storage = useServerStorage('user');
  const isLandingPage = storage.data.analytics?.landingPageId === params.id;

  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('uninitialized');

  const [readQuickViewQuery, { quickView }] =
    analyticsApi.useLazyReadQuickViewQuery({
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        quickView: data?.data.quick_view,
      }),
    });

  useEffect(() => {
    const readQuickView = async () => {
      setAsyncStatus('pending');

      const {
        data: { quick_view: quickView },
      } = await readQuickViewQuery({
        id: params.id,
      }).unwrap();

      if (quickView) {
        dispatch(analyticsActions.setQuickView({ id: quickView.id }));

        /** Whether the quick view has panels pinned from multiple dashboards. */
        const isCustom =
          new Set(quickView.panels.map((panel) => panel.dashboard_id)).size > 1;

        ampli.analyticsQuickViewViewed({
          shared: quickView.shared,
          isLandingPage,
          isCustom,
        });

        setAsyncStatus('fulfilled');
      }
    };

    readQuickView().catch((e) => {
      const error = e as unknown as {
        status: ApiErrorStatusCode;
        data: {
          errors: string[];
        };
      };

      // If the requested quick view is set as the landing page and it has been deleted,
      // remove it as the landing page and redirect to the default dashboard instead.
      if (isLandingPage && error.status === ApiErrorStatusCode.NotFound) {
        storage.set({
          analytics: (draft) => {
            delete draft.landingPageId;
          },
        });

        navigate(
          { path: '/analytics/dashboard/:slug', params: {} },
          { replace: true },
        );
      } else {
        Sentry.captureException(error, {
          tags: { team: SentryTeam.Insights },
        });
        setAsyncStatus('rejected');
      }
    });
    // Only run the effect when the URL path params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  return useMemo(
    () => ({
      quickView,
      asyncStatus,
    }),
    [quickView, asyncStatus],
  );
};
